import React, { useEffect, useState } from "react";
import { Col, Row, Form, Modal } from "react-bootstrap";
import PropTypes from 'prop-types';
import { Button, makeStyles, SvgIcon } from "@material-ui/core";
import { useForm } from 'react-hook-form/dist/react-hook-form.ie11';
import { TextInput, SelectInput, StyledRadioInput } from '../../../Helpers/Inputs';
import EmojiObjectsIcon from '@material-ui/icons/EmojiObjects';
import { number, object, string } from 'yup';
import Age from '../../../Helpers/Dates/Age';
import NeedsAnalysisFooter from "../../../Tools/NeedsAnalysis/NeedsAnalysisFooter";
import AnalysisData from "../../../Helpers/Constants/AnalysisData";
import { getSessionStorageItem, SESSION_STORAGE_ANALYSIS_KEY, SESSION_STORAGE_BASIC_INFO_KEY } from "../../../../sessionStorage";
import "./TermLengthCalc.scss";

const white = "#ffffff";
const offWhite = "rgba(255, 255, 255, 0.7)";

const useStyles = makeStyles((theme) => ({
  buttonSelected: {
    borderColor: theme.palette.primary.main,
  },
  svgIcon: {
    '& > g': {
      stroke: theme.palette.primary.main,
    }
  },
  spanTip: {
    color: theme.palette.primary.main,
  },
  white: {
    color: white,
    border: `2px solid ${white}`,
    '&:hover': {
      color: offWhite,
      borderColor: offWhite
    },
    '&:focus': {
      boxShadow: `0 0 5px 0px ${offWhite}`,
      outline: 'none'
    }
  }
}));

export default function TermLengthAnalysis(props) {

  const basicInfo = getSessionStorageItem(SESSION_STORAGE_BASIC_INFO_KEY);
  const sessionStorageFormState = getSessionStorageItem(SESSION_STORAGE_ANALYSIS_KEY);
  const classes = useStyles();

  const initialFormState = sessionStorageFormState || AnalysisData();

  const [formValues, setFormValues] = useState(initialFormState);
  const [suggestedTermLength, setSuggestedTermLength] = useState(null);

  const { register, errors, triggerValidation, setValue, handleSubmit } = useForm({
    validationSchema: TermLengthAnalysis.ValidationSchema(Age(basicInfo && basicInfo.birthdate)),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  const calculateTermLength = () => {
    let termLength = 20;
    const insuredAge = (props.fields && props.fields.insuredAge) || null;

    if (insuredAge) {
      termLength = (insuredAge >= 60) ? 10 : 20;
    }

    setSuggestedTermLength(termLength);
  };

  const applyTermLength = () => {
    if (suggestedTermLength === null || !props.onTermLengthCalculation instanceof Function) {
      return;
    }

    props.onTermLengthCalculation(suggestedTermLength);
  };

  useEffect(() => {
    register({ name: "maritalStatus" });
    setValue('maritalStatus', formValues.maritalStatus);
    register({ name: "numberOfChildren" });
    setValue('numberOfChildren', formValues.numberOfChildren);
    register({ name: "childAge" });
    setValue('childAge', formValues.childAge);
    register({ name: "mortgage" });
    setValue('mortgage', formValues.mortgage);
    register({ name: "mortgageTerm" });
    setValue('mortgageTerm', formValues.mortgageTerm);
    register({ name: "longestDebt" });
    setValue('longestDebt', formValues.longestDebt);
  }, [register]);

  useEffect(() => {
    calculateTermLength();
  }, [formValues]);

  const handleInputChange = (fieldName, value) => {
    setValue(fieldName, value);
    const formStateToUpdate = { ...formValues };
    formStateToUpdate[fieldName] = value;
    setFormValues({ ...formStateToUpdate });
    triggerValidation(fieldName);
    props.setAnalysisFormValues({...formStateToUpdate});
  };

  const handleChildrenNumberChange = (fieldName, value) => {
    const newChild = formValues.numberOfChildren === "" ? 0 : formValues.numberOfChildren;
    const formStateToUpdate = { ...formValues };

    if (newChild === 0 || newChild < value) {
      for (let i = newChild; i < value; i++) {
        formStateToUpdate.children.push({ childAge: '' });
        register({ name: `children[${i}].childAge` });
      }
    } else {
      const len = newChild - 1;
      for (let i = len; i >= value; i--) {
        formStateToUpdate.children.splice(i, 1);
      }
    }

    formStateToUpdate[fieldName] = value;

    setFormValues({ ...formStateToUpdate });
    setValue(fieldName, value);
    props.setAnalysisFormValues({ ...formStateToUpdate });
  };

  function handleClick() {
    applyTermLength();
    sessionStorage.setItem(SESSION_STORAGE_ANALYSIS_KEY, JSON.stringify({ ...formValues }));
    props.fields.setModalTermLengthCalculatorIsOpen(false);
  }

  return (
    <React.Fragment>
      <TermLengthAnalysis.ModalHeroImageHeader suggestedTermLength={suggestedTermLength} isPrequote={props.isPrequote}
                                               onButtonClick={() => handleClick()}/>
      <Form className='modal-scroll' onSubmit={() => handleSubmit(handleClick())}>
        <Row className="terms-calculator justify-content-center">
          <Col lg={8} md={12}>
            <Row>
              <Form.Group className="mb-0 mt-4">
                <Col lg={12}>
                  <StyledRadioInput
                    name="maritalStatus"
                    id="maritalStatus"
                    value={formValues.maritalStatus}
                    options={[{ value: "Married", label: "Married" }, { value: "Single", label: "Single" }]}
                    onChange={e => handleInputChange('maritalStatus', e)}
                    errors={errors && errors.maritalStatus}
                    isWhiteBackground={true}
                    isBlankIcon={true}
                  />
                </Col>
              </Form.Group>
            </Row>
            <Row>
              <Form.Group>
                <Col lg={12}>
                  <SelectInput
                    name="numberOfChildren"
                    id="numberOfChildren"
                    value={formValues.numberOfChildren}
                    onChange={(e) => handleChildrenNumberChange('numberOfChildren', e.target.value)}
                    label="Number of Children"
                    selectOptions={[
                      { value: 0, label: "None" },
                      { value: 1, label: "1" },
                      { value: 2, label: "2" },
                      { value: 3, label: "3" },
                      { value: 4, label: "4" },
                      { value: 5, label: "5" },
                      { value: 6, label: "6" },
                      { value: 7, label: "7" },
                      { value: 8, label: "8" },
                    ]}
                    errors={errors.numberOfChildren}
                    inputRef={register}
                  />
                </Col>
              </Form.Group>
            </Row>
            {formValues.numberOfChildren > 0 &&
              <Row>
                <Form.Group>
                  <Col lg={12}>
                    <TextInput
                      name="childAge"
                      id="childAge"
                      label="Age of Youngest Child"
                      value={formValues.childAge}
                      errors={errors && errors.childAge}
                      inputRef={register}
                      onChange={(e) => handleInputChange('childAge', e.target.value)}
                    />
                  </Col>
                </Form.Group>
              </Row>
            }
            <Row>
              <Form.Group>
                <Col lg={12}>
                  <StyledRadioInput
                    name="mortgage"
                    id="mortgage"
                    label='Do you have a mortgage?'
                    value={formValues.mortgage}
                    options={[{ value: "Yes", label: "Yes" }, { value: "No", label: "No" }]}
                    onChange={e => handleInputChange('mortgage', e)}
                    errors={errors && errors.mortgage}
                    isWhiteBackground={true}
                    isBlankIcon={true}
                  />
                </Col>
              </Form.Group>
            </Row>
            {formValues.mortgage === "Yes" && (
              <React.Fragment>
                <Row>
                  <Form.Group>
                    <Col lg={12}>
                      <TextInput
                        name="mortgageTerm"
                        id="mortgageTerm"
                        label="Remaining Mortgage Term (Years)"
                        value={formValues.mortgageTerm}
                        errors={errors && errors.mortgageTerm}
                        inputRef={register}
                        onChange={(e) => handleInputChange('mortgageTerm', e.target.value)}
                      />
                    </Col>
                  </Form.Group>
                </Row>
                <div className="tooltip-box mb-2">
              <span className={classes.spanTip}><SvgIcon style={{ fontSize: '1rem', marginTop: '-4px' }} color="primary"
                                                         component={EmojiObjectsIcon}/>Insider Tip:</span> Most people
                  choose to protect the remaining balance and years remaining on their mortgage.
                </div>
              </React.Fragment>
            )}
            <Row className="mt-2">
              <Form.Group>
                <Col lg={12}>
                  <TextInput
                    name="longestDebt"
                    id="longestDebt"
                    label="Longest Remaining Debt (Years)"
                    value={formValues.longestDebt}
                    errors={errors && errors.longestDebt}
                    inputRef={register}
                    onChange={(e) => handleInputChange('longestDebt', e.target.value)}
                  />
                </Col>
              </Form.Group>
            </Row>
            <Row className="justify-content-center">
              <Form.Group>
                <Col lg={12}>
                  <Button type="button" color="primary" variant="contained" onClick={() => handleClick()} fullWidth>
                    UPDATE QUOTE
                  </Button>
                </Col>
              </Form.Group>
            </Row>
          </Col>
        </Row>
        < NeedsAnalysisFooter isCustomTerm={props.isCustomTerm}/>
      </Form>
    </React.Fragment>
  );
}

TermLengthAnalysis.ModalHeroImageHeader = function (props) {

  const classes = useStyles();

  return (
    <Modal.Header closeButton>
      <div className="modalImageHeader">
        <div className="termCalculatorHeroImage" />
        <div className="headerModalDiv">
          <p id="coverageNeed">My Insurance Term</p>
          <p id="coverageNeed"><b>{props.suggestedTermLength ? props.suggestedTermLength : '/'} years of coverage</b>
          </p>
          <Button type="button"
                  variant="outlined"
                  onClick={() => props.onButtonClick()}
                  className={`btn-wide-sm ${classes.white}`}>UPDATE QUOTE</Button>
        </div>
      </div>
    </Modal.Header>
  );
};

TermLengthAnalysis.propTypes = {
  fields: PropTypes.object,
  isPrequote: PropTypes.bool,
  onTermLengthCalculation: PropTypes.func,
  isCustomTerm: PropTypes.bool,
};

TermLengthAnalysis.ModalHeroImageHeader.propTypes = {
  onButtonClick: PropTypes.func,
  suggestedTermLength: PropTypes.number,
};

TermLengthAnalysis.ValidationSchema = (currentAge) => {
  return object().shape({
    maritalStatus: string().notRequired(),
    numberOfChildren: string().notRequired(),
    childAge: string().when('numberOfChildren', {
      is: (numberOfChildren) => numberOfChildren > 0,
      then: string().required("Child Age is required").max(2, 'Invalid Age.').test({
        message: 'Invalid Age.',
        test: (value) => value >= 0
      }).test({
        message: `Child's age cannot be greater than parent's age.`,
        test: (value) => isNaN(currentAge) ? true : currentAge > value
      }),
      otherwise: string().notRequired()
    }),
    mortgage: string().notRequired(),
    mortgageTerm: number().transform((v) => (v === '' || v === null || v === undefined) ? 0 : v)
      .when('mortgage', {
        is: (mortgage) => mortgage.toLowerCase() === 'yes',
        then: number().required('Enter remaining mortgage term in years')
          .typeError('Enter remaining mortgage term in number of years')
          .min(1, 'Mortgage term must be greater than one year')
          .max(40, 'Mortgage term must not be greater than 40 years')
      }),
    longestDebt: number().transform((v) => (v === '' || v === null || v === undefined) ? 0 : v)
      .typeError('Enter remaining debt in number of years')
      .min(1, 'Longest remaining debt must be greater than one year')
      .max(100, 'Longest remaining debt must not be greater than 100 years')
  });
};



// WEBPACK FOOTER //
// ./src/components/Quoters/CustomizeTerm/TermLengthCalculator/TermLengthAnalysis.js